<script setup lang="ts">
	import {
		Dialog,
		DialogPanel,
		DialogTitle,
		TransitionChild,
		TransitionRoot,
	} from '@headlessui/vue'
	import { XMarkIcon } from '@heroicons/vue/24/outline'

	import HasPermission from './HasPermission.vue'

	interface Props {
		when?: boolean
		title?: string
		updatePermission?: string
		createPermission?: string
		entityType?: string
		iconClose?: string
		hideButtons?: boolean
		nameButtonClose?: string
		hideButtonSave?: boolean
		panelMaxWidth?: string
	}
	interface Emits {
		(e: 'update:closeSlideOver', value: boolean): boolean
		(e: 'update:saveSlideOver'): void
	}

	const {
		when = false,
		hideButtons = false,
		panelMaxWidth = 'max-w-md',
	} = defineProps<Props>()
	const emit = defineEmits<Emits>()

	const closeSlideOver = (value: boolean) => {
		emit('update:closeSlideOver', value)
	}
	const saveSlideOver = () => {
		emit('update:saveSlideOver')
	}
</script>

<template>
	<Teleport to="body">
		<TransitionRoot
			as="template"
			class="z-30"
			:show="when"
		>
			<div>
				<div
					v-if="when"
					class="fixed inset-0 bg-gray-800 opacity-50 z-20"
				></div>
				<Dialog
					as="div"
					class="relative z-30 outline-none"
					@click="closeSlideOver(false)"
					@close="closeSlideOver(false)"
				>
					<div class="fixed inset-0" />

					<div class="fixed inset-0 overflow-hidden">
						<div class="absolute inset-0 overflow-hidden">
							<div
								class="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10 sm:pl-16"
							>
								<TransitionChild
									as="template"
									enter="transform transition ease-out duration-150"
									enter-from="translate-x-full"
									enter-to="translate-x-0"
									leave="transform transition ease-in duration-150"
									leave-from="translate-x-0"
									leave-to="translate-x-full"
								>
									<DialogPanel
										:class="[
											'pointer-events-auto w-screen outline-none',
											panelMaxWidth,
										]"
									>
										<form
											class="flex h-full flex-col divide-y divide-gray-200 bg-white shadow-xl outline-none"
										>
											<div class="h-0 flex-1 overflow-y-auto">
												<div
													class="px-4 py-6 sm:px-6 flex items-center justify-between"
												>
													<div class="flex items-center justify-between w-full">
														<div class="ml-3 flex h-7 items-center">
															<button
																type="button"
																class="relative rounded-md text-gray-600 hover:text-gray-800"
																@click="closeSlideOver(false)"
															>
																<span class="absolute -inset-2.5" />
																<span class="sr-only">Close panel</span>

																<div v-if="iconClose">
																	<span class="material-symbols-outlined">
																		{{ iconClose }}
																	</span>
																</div>
																<div v-else>
																	<XMarkIcon
																		class="h-6 w-6"
																		aria-hidden="true"
																	/>
																</div>
															</button>
														</div>
														<div>
															<DialogTitle
																class="leading-6 text-[#8E8E93] font-semibold text-lg"
																>{{ $props.title }}
															</DialogTitle>
														</div>

														<div>
															<slot name="other-action-header" />
														</div>
													</div>
												</div>
												<div>
													<slot></slot>
												</div>
											</div>
											<div
												class="flex flex-shrink-0 justify-end px-4 py-4"
												v-if="!hideButtons"
											>
												<button
													type="button"
													class="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
													@click="closeSlideOver(false)"
												>
													{{ nameButtonClose || 'Cancelar' }}
												</button>

												<HasPermission
													:name-permission="
														createPermission || updatePermission || ''
													"
													:entity-type="entityType || ''"
												>
													<button
														type="submit"
														@click.prevent="saveSlideOver"
														class="ml-4 inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
													>
														Guardar
													</button>
												</HasPermission>
											</div>

											<div v-else>
												<slot name="footer" />
											</div>
										</form>
									</DialogPanel>
								</TransitionChild>
							</div>
						</div>
					</div>
				</Dialog>
			</div>
		</TransitionRoot>
	</Teleport>
</template>
